import React, { useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Paragraph from "@tiptap/extension-paragraph";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import Underline from "@tiptap/extension-underline";
import { Extension } from "@tiptap/core";
import { IconButton, Tooltip } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import LooksOneIcon from "@mui/icons-material/LooksOne";
import LooksTwoIcon from "@mui/icons-material/LooksTwo";
import Looks3Icon from "@mui/icons-material/Looks3";
import TableChartIcon from "@mui/icons-material/TableChart";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import TableRowsIcon from "@mui/icons-material/TableRows";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ImageIcon from "@mui/icons-material/Image";

const CustomTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      useradded: {
        default: false,
        parseHTML: element => element.getAttribute("data-useradded") === "true",
        renderHTML: attributes => {
          if (!attributes.useradded) return {};
          return { "data-useradded": "true" };
        },
      },
    };
  },
});

const TextAlign = Extension.create({
  addOptions() {
    return {
      types: ['paragraph'],
      alignments: ['left', 'center', 'right'],
      defaultAlignment: 'left',
    };
  },
  
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          textAlign: {
            default: this.options.defaultAlignment,
            parseHTML: element => element.style.textAlign || this.options.defaultAlignment,
            renderHTML: attributes => {
              if (attributes.textAlign === this.options.defaultAlignment) {
                return {};
              }
              return { style: `text-align: ${attributes.textAlign}` };
            },
          },
        },
      },
    ];
  },
  
  addCommands() {
    return {
      setTextAlign: (alignment) => ({ commands }) => {
        if (!this.options.alignments.includes(alignment)) {
          return false;
        }
        return this.options.types.every(type => 
          commands.updateAttributes(type, { textAlign: alignment })
        );
      },
      unsetTextAlign: () => ({ commands }) => {
        return this.options.types.every(type => 
          commands.resetAttributes(type, 'textAlign')
        );
      },
    };
  },
});

const TiptapControlledEditor = ({ initialContent = "<p>Start writing...</p>", onChange }) => {
  const [content, setContent] = useState(initialContent);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ paragraph: false }),
      Paragraph.configure({ HTMLAttributes: { style: "margin:0; padding:0;" } }),
      Link,
      Image.configure({ allowBase64: true }),
      CustomTable.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Underline,
      TextAlign,
    ],
    content,
    onUpdate({ editor }) {
      const html = cleanHTML(editor.getHTML());
      setContent(html);
      onChange?.(html);
    },
  });

  if (!editor) return null;

  const cleanHTML = (html) => {
    html = html.replace(/<p>(?:<br\s*\/?>|\s*)<\/p>/g, "");
    html = html.replace(/<p>\s*<\/p>/g, "");
    html = html.replace(/(<br\s*\/?>){2,}/g, "<br>");
    return html.trim();
  };

  const addImageFromDesktop = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      editor.chain().focus().setImage({ src: reader.result }).run();
    };
    reader.readAsDataURL(file);
  };

  const insertUserTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();

    const { state, view } = editor;
    const { selection } = state;

    const $pos = state.doc.resolve(selection.from);
    const tableNode = $pos.node($pos.depth);

    const dom = view.domAtPos(selection.from).node;
    const tableDom = dom.closest("table");
    if (tableDom) tableDom.classList.add("user_added_table");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div style={{
        backgroundColor: "#f8fafc",
        padding: "8px",
        borderBottom: "1px solid var(--border-color)",
        display: "flex",
        flexWrap: "wrap",
        gap: "4px",
        alignItems: "center"
      }}>
        {/* Text Formatting Group */}
        <div style={{ display: "flex", alignItems: "center", gap: "2px", paddingRight: "8px", marginRight: "4px", borderRight: "1px solid #cbd5e1" }}>
          <Tooltip title="Bold">
            <IconButton size="small" onClick={() => editor.chain().focus().toggleBold().run()} sx={{ padding: "6px", borderRadius: "6px", "&:hover": { backgroundColor: "#e2e8f0" } }}>
              <FormatBoldIcon sx={{ fontSize: 20, color: "#64748b" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Italic">
            <IconButton size="small" onClick={() => editor.chain().focus().toggleItalic().run()} sx={{ padding: "6px", borderRadius: "6px", "&:hover": { backgroundColor: "#e2e8f0" } }}>
              <FormatItalicIcon sx={{ fontSize: 20, color: "#64748b" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Strike">
            <IconButton size="small" onClick={() => editor.chain().focus().toggleStrike().run()} sx={{ padding: "6px", borderRadius: "6px", "&:hover": { backgroundColor: "#e2e8f0" } }}>
              <StrikethroughSIcon sx={{ fontSize: 20, color: "#64748b" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Underline">
            <IconButton size="small" onClick={() => editor.chain().focus().toggleUnderline().run()} sx={{ padding: "6px", borderRadius: "6px", "&:hover": { backgroundColor: "#e2e8f0" } }}>
              <FormatUnderlinedIcon sx={{ fontSize: 20, color: "#64748b" }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Alignment Group */}
        <div style={{ display: "flex", alignItems: "center", gap: "2px", paddingRight: "8px", marginRight: "4px", borderRight: "1px solid #cbd5e1" }}>
          <Tooltip title="Align Left">
            <IconButton size="small" onClick={() => editor.chain().focus().setTextAlign('left').run()} sx={{ padding: "6px", borderRadius: "6px", "&:hover": { backgroundColor: "#e2e8f0" } }}>
              <FormatAlignLeftIcon sx={{ fontSize: 20, color: "#64748b" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Align Center">
            <IconButton size="small" onClick={() => editor.chain().focus().setTextAlign('center').run()} sx={{ padding: "6px", borderRadius: "6px", "&:hover": { backgroundColor: "#e2e8f0" } }}>
              <FormatAlignCenterIcon sx={{ fontSize: 20, color: "#64748b" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Align Right">
            <IconButton size="small" onClick={() => editor.chain().focus().setTextAlign('right').run()} sx={{ padding: "6px", borderRadius: "6px", "&:hover": { backgroundColor: "#e2e8f0" } }}>
              <FormatAlignRightIcon sx={{ fontSize: 20, color: "#64748b" }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* List Group */}
        <div style={{ display: "flex", alignItems: "center", gap: "2px", paddingRight: "8px", marginRight: "4px", borderRight: "1px solid #cbd5e1" }}>
          <Tooltip title="Bullet List">
            <IconButton size="small" onClick={() => editor.chain().focus().toggleBulletList().run()} sx={{ padding: "6px", borderRadius: "6px", "&:hover": { backgroundColor: "#e2e8f0" } }}>
              <FormatListBulletedIcon sx={{ fontSize: 20, color: "#64748b" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Ordered List">
            <IconButton size="small" onClick={() => editor.chain().focus().toggleOrderedList().run()} sx={{ padding: "6px", borderRadius: "6px", "&:hover": { backgroundColor: "#e2e8f0" } }}>
              <FormatListNumberedIcon sx={{ fontSize: 20, color: "#64748b" }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Insert Group */}
        <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
          <Tooltip title="Add Image">
            <IconButton size="small" onClick={() => document.getElementById("imageUpload").click()} sx={{ padding: "6px", borderRadius: "6px", "&:hover": { backgroundColor: "#e2e8f0" } }}>
              <ImageIcon sx={{ fontSize: 20, color: "#64748b" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Insert Table">
            <IconButton size="small" onClick={insertUserTable} sx={{ padding: "6px", borderRadius: "6px", "&:hover": { backgroundColor: "#e2e8f0" } }}>
              <TableChartIcon sx={{ fontSize: 20, color: "#64748b" }} />
            </IconButton>
          </Tooltip>
        </div>

        <input
          type="file"
          id="imageUpload"
          style={{ display: "none" }}
          accept="image/*"
          onChange={addImageFromDesktop}
        />
      </div>

      <div className="editor-content-wrapper" style={{
        minHeight: "500px",
        maxHeight: "500px",
        overflowY: "auto",
        padding: "32px",
        backgroundColor: "white"
      }}>
        <EditorContent editor={editor} className="tiptap-editor" />
      </div>

      <style>
        {`
.editor-content-wrapper::-webkit-scrollbar {
  width: 6px;
}

.editor-content-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.editor-content-wrapper::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 10px;
}

.tiptap-editor .ProseMirror {
  outline: none;
  min-height: 436px;
  max-width: 100%;
  font-family: 'Inter', 'Helvetica', 'Arial', sans-serif;
  font-size: 15px;
  line-height: 1.6;
  color: #334155;
}

.tiptap-editor .ProseMirror p {
  margin: 0 0 12px 0;
}

.tiptap-editor .ProseMirror h1,
.tiptap-editor .ProseMirror h2,
.tiptap-editor .ProseMirror h3 {
  font-weight: 700;
  margin: 16px 0 12px 0;
  color: #1e293b;
}

.tiptap-editor .ProseMirror h1 {
  font-size: 24px;
}

.tiptap-editor .ProseMirror h2 {
  font-size: 20px;
}

.tiptap-editor .ProseMirror h3 {
  font-size: 18px;
}

.tiptap-editor .ProseMirror ul,
.tiptap-editor .ProseMirror ol {
  padding-left: 24px;
  margin: 12px 0;
}

.tiptap-editor .ProseMirror li {
  margin: 4px 0;
}

/* Remove borders from all tables by default */
.tiptap-editor .ProseMirror table,
.tiptap-editor .ProseMirror th,
.tiptap-editor .ProseMirror td {
  border: none !important;
  padding: 8px !important;
  vertical-align: top;
  word-break: break-word;
  border-collapse: collapse !important;
  table-layout: fixed !important;
}

.tiptap-editor .ProseMirror img {
  object-fit: cover !important;
  display: block !important;
  max-width: 100%;
  height: auto;
  margin: 16px 0;
  border-radius: 8px;
}

.tiptap-editor .ProseMirror table.user_added_table,
.tiptap-editor .ProseMirror table.user_added_table th,
.tiptap-editor .ProseMirror table.user_added_table td {
  border: 1px solid #cbd5e1 !important;
  padding: 8px !important;
}

.tiptap-editor .ProseMirror table.user_added_table {
  margin: 16px 0;
  width: 100%;
}

.tiptap-editor .ProseMirror a {
  color: var(--primary);
  text-decoration: underline;
}
        `}
      </style>
    </div>
  );
};

export default TiptapControlledEditor;
